import { useFormikContext } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { MdInfoOutline, MdOutlineSearch } from 'react-icons/md'

import type { ReactElement, ChangeEvent } from 'react'
import type { Tab } from '~/components/VerticalTabs'
import type { IAssetOption, ITranslationOption } from '~/types/option'
import type { IPageContainerFormError, IPlaceholderFilters } from '~/types/page-container'
import type { IPageContainerFormValues, ITemplateConfigOptions } from '~/types/template'

import PageContainerCompletion from './Completion'
import PlaceholderForm from './Form'

import Banner from '~/components/Banner'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Tooltip from '~/components/Tooltip'
import VerticalTabs from '~/components/VerticalTabs'
import { isDeepEqual } from '~/utils/object'
import {
  filterPlaceholdersByCategory,
  filterPlaceholdersConfig,
  getAssetErrorMessage,
  getAssetErrors,
  getSubTabs,
  getTranslationErrorMessage,
} from '~/utils/page-container'
import { firstCharToUpperCase } from '~/utils/string'

import './styles.scss'

interface Props {
  onFieldClick: (placeholderKey: string) => void
  pageIndex: number
  template: ITemplateConfigOptions
}

const getErrorMessage = (errors: IPageContainerFormError[]) => {
  return (
    <div>
      {errors.map(({ error, key }, index) => (
        <div key={index}>
          {key}: {error}
        </div>
      ))}
    </div>
  )
}

const Editor = ({ pageIndex, template }: Props): ReactElement => {
  const {
    getFieldMeta,
    setFieldValue,
    values: { focus, placeholders, templateFilter },
  } = useFormikContext<IPageContainerFormValues>()

  const unsavedChanges = !isDeepEqual(getFieldMeta('placeholders').value, getFieldMeta('placeholders').initialValue)

  const [searchText, setSearchText] = useState<string>('')

  const [filters, setFilters] = useState<IPlaceholderFilters>({ errorOnly: false, uncompletedOnly: false })

  const errors = placeholders.reduce(
    (prev, { key, rules, type, value }) => {
      if (type === 'translation' && !!value) {
        const translation = (value as ITranslationOption).translation
        const error = translation && getTranslationErrorMessage({ rules, translation })
        error && prev.push({ error, key })
      }

      if (type === 'asset' && !!value) {
        const errors = getAssetErrors({ asset: value as IAssetOption, rules })
        if (errors) {
          const { qualityIdentifiers } = errors
          qualityIdentifiers.length > 0 && prev.push({ error: getAssetErrorMessage({ qualityIdentifiers }), key })
        }
      }
      return prev
    },
    [] as { error: string; key: string }[]
  )

  const tabsData = useMemo((): Tab[] => {
    const { placeholders: placeholdersConfig } = template.config[pageIndex]

    const filteredPlaceholders = filterPlaceholdersConfig({
      errors,
      filters,
      placeholderFormValues: placeholders,
      placeholdersConfig,
      searchText,
      templateFilter,
    })

    const categories = [
      ...new Set(
        placeholdersConfig
          .filter(({ category }) => filterPlaceholdersByCategory(filteredPlaceholders, category).length > 0)
          .map(({ category }) => category)
      ),
    ].sort((a, b) => {
      return a.localeCompare(b)
    })

    return categories.map((category) => {
      const subTabs = getSubTabs(filteredPlaceholders, category, placeholders, errors)
      const itemsCount = subTabs.filter(({ isChecked }) => !isChecked).length

      return {
        isChecked: itemsCount === 0,
        itemsCount,
        label: firstCharToUpperCase(category),
        name: category,
        subTabs,
      }
    })
  }, [placeholders, searchText, filters, pageIndex, template, templateFilter, errors])

  const defaultTab = tabsData?.length ? tabsData[0] : undefined

  const defaultSubTab = defaultTab?.subTabs?.length ? defaultTab.subTabs[0] : undefined

  const [currentTab, setCurrentTab] = useState<Tab['name']>(defaultTab?.name || '')

  const [currentSubTab, setCurrentSubTab] = useState<string | undefined>(defaultSubTab?.name || '')

  const handleClickTab = (tabName: string) => {
    setCurrentTab(tabName as Tab['name'])
    const subTabs = tabsData.find(({ name }) => name === tabName)?.subTabs
    subTabs?.length && setCurrentSubTab(subTabs[0].name)
  }

  const handleSubTabClick = (placeholderKey: string) => {
    setCurrentSubTab(placeholderKey)
    setFieldValue('focus', placeholderKey)
  }

  const handleTextChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearchText(evt.target.value)
  }

  const handleEmptyPlaceholderCheckboxChange = () => {
    setFilters((filters) => {
      return {
        ...filters,
        uncompletedOnly: !filters?.uncompletedOnly,
      }
    })
  }

  const handleErrorPlaceholderCheckboxChange = () => {
    setFilters((filters) => {
      return {
        ...filters,
        errorOnly: !filters?.errorOnly,
      }
    })
  }

  useEffect(() => {
    if (focus) {
      const tabs = tabsData.find(({ subTabs = [] }) => subTabs.some(({ name }) => name === focus))
      tabs && setCurrentTab(tabs.name)
      tabs?.subTabs && setCurrentSubTab(focus)
    }
  }, [focus, tabsData])

  useEffect(() => {
    const tab = tabsData.find(({ name }) => name === currentTab)
    const subTabs = tab?.subTabs || []

    if (!tab) {
      const defaultTab = tabsData.length ? tabsData[0] : undefined
      defaultTab && setCurrentTab(defaultTab.name)
      defaultTab?.subTabs?.length && setCurrentSubTab(defaultTab.subTabs[0].name)
    } else if (!subTabs.find(({ name }) => name === currentSubTab)) {
      subTabs.length && setCurrentSubTab(subTabs[0].name)
    }
  }, [tabsData, currentTab, currentSubTab])

  useEffect(() => {
    if (filters.errorOnly && errors.length === 0) {
      setFilters((filters) => {
        return {
          ...filters,
          errorOnly: false,
        }
      })
    }
  }, [errors, filters])

  return (
    <div className="page-container-editor-component">
      <div className="editor-header">
        <div className="placeholder-filters">
          <TextInput
            icon={<MdOutlineSearch size={10} />}
            onChange={handleTextChange}
            placeholder="Placeholder name"
            size="small"
            value={searchText}
            wrapperClassName="placeholder-filters__search"
          />
          <CheckboxInput label="Uncompleted only" onChange={handleEmptyPlaceholderCheckboxChange} />
          {errors.length > 0 ? (
            <div className="placeholder-filters__errors">
              <CheckboxInput label={`Show ${errors.length} error(s)`} onChange={handleErrorPlaceholderCheckboxChange} />
              <Tooltip
                align={{ points: ['bl', 'tl'] }}
                mouseEnterDelay={0.2}
                overlay={getErrorMessage(errors)}
                placement="topRight"
                trigger={['hover']}
              >
                <div className="warning-tooltip-component">
                  <MdInfoOutline color="#ca3423" size={16} />
                </div>
              </Tooltip>
            </div>
          ) : null}
          {unsavedChanges && <Banner variant="warning">You have unsaved changes</Banner>}
        </div>
        <PageContainerCompletion />
      </div>
      <div className="editor-body">
        <VerticalTabs
          className="placeholder-type-tabs"
          currentSubTab={currentSubTab}
          currentTab={currentTab}
          filter={searchText}
          onClickTab={handleClickTab}
          onSubTabClick={handleSubTabClick}
          tabsData={tabsData}
        />
        {tabsData.length ? (
          <div className="editor-body__placeholders">
            <div className="editor-body__placeholders__title">{firstCharToUpperCase(currentTab)}</div>
            <PlaceholderForm
              onScroll={(placeholderKey) => setCurrentSubTab(placeholderKey)}
              placeholders={placeholders.filter(({ key }) => {
                const tab = tabsData.find(({ name }) => name === currentTab)
                const subTabs = tab?.subTabs
                return subTabs?.some(({ name }) => name === key)
              })}
            />
          </div>
        ) : (
          <div className="editor-body__placeholders">
            <div className="editor-body__placeholders__title">No placeholder found.</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Editor
